import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useConnection } from '@/entities/connection';
import { DatasetScorePanel, useDatasetScore } from '@/features/scoring';
import {
  RunBindingButton,
  ruleVersionsQuery,
  severityTone,
  useBindings,
  useRules,
} from '@/features/rules';
import { useRequestProfile } from '@/features/profiler';

import { useDatasetFacts } from '../api/catalogue';
import { bindingCount, datasetSummaryClauses, profileReading } from '../model/catalogue';
import { nameGlobMatches } from '../model/selectors';

import {
  diffVersions,
  qualifiedName,
  tracksRenames,
  verdictStillApplies,
} from '@/entities/dataset';
import type { Classification, DatasetVersion } from '@/shared/api/generated/catalog';
import { ProfileBaselineCard, ProfilePanel } from '@/features/profiler';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { LoadingState, UnavailableState } from '@/shared/ui/States';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SCREEN_PATHS } from '@/shared/routes';
import { Tabs } from '@/shared/ui/Tabs';

import {
  NOT_CLASSIFIED,
  useClassification,
  useDataset,
  useRequestClassification,
  useSchemaHistory,
} from '../api/catalog';

/* The trail back up the drill-down: all connections / this source / this dataset.
 *
 * **The middle crumb is the point of this component.** A dataset is reached through its source now,
 * so a two-crumb trail would drop the one level a reader most wants to step back to — the sibling
 * tables of the same database. `source` is the connection's own facts, so the crumb is a real name
 * rather than an id.
 *
 * `source` may be absent, and the crumb is then *omitted rather than rendered as an id*. Three
 * things produce that: the connection read is still in flight, it failed, or the id is past the
 * listing's 200-row cap. None of the three justifies putting a UUID in a breadcrumb, which is the
 * bare-id defect this codebase has shipped four times — and a trail of two crumbs is a smaller loss
 * than a crumb nobody can read.
 */
function Breadcrumbs({
  schema,
  source,
}: {
  /** The dataset's schema, which is the path *to* it. Not the dataset's own name. */
  readonly schema: string;
  readonly source: { readonly id: string; readonly name: string } | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
      <Link to={SCREEN_PATHS.datasets}>{t('catalogue.crumb.allSources')}</Link>
      <span aria-hidden="true" className="lq-breadcrumbs__sep">
        /
      </span>
      {source === null ? null : (
        <>
          <Link to={`${SCREEN_PATHS.datasets}/source/${source.id}`}>{source.name}</Link>
          <span aria-hidden="true" className="lq-breadcrumbs__sep">
            /
          </span>
        </>
      )}
      {/* **The schema, not the dataset**, and the reason is what a breadcrumb is for: it names the
          path *to* here, and the dataset's own name is already the page's heading two lines below.
          Repeating it makes the trail end in the thing you are looking at, which tells a reader
          nothing they cannot see.

          Not a link. This platform has no per-schema screen — a harvest groups by schema and
          nothing routes to one — so the crumb is a label rather than a dead control, which is the
          same call `aria-current="page"` makes for a real last crumb. */}
      <span aria-current="page">{schema}</span>
    </nav>
  );
}

/* The PII panel.
 *
 * The rule it enforces: only an APPROVED verdict with zero findings may read as "no PII found".
 * Every other empty verdict means nobody looked or the look failed, and those are not the same
 * thing. Getting this wrong is how a column of Aadhaar numbers ends up on a dashboard someone
 * trusts.
 */
function PiiPanel({
  classification,
  latest,
  datasetId,
}: {
  readonly classification: Classification | typeof NOT_CLASSIFIED;
  readonly latest: DatasetVersion | undefined;
  readonly datasetId: string;
}): ReactElement {
  const { t } = useTranslation();
  const requestClassification = useRequestClassification(datasetId);

  if (classification === NOT_CLASSIFIED) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          alignItems: 'flex-start',
        }}
      >
        {requestClassification.isError && (
          <Alert tone="error">{requestClassification.error.message}</Alert>
        )}
        {requestClassification.isSuccess && (
          <Alert tone="notice">{t('pii.classifySuccess')}</Alert>
        )}
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('dataset.pii.never')}
        </p>
        <Button
          tone="primary"
          busy={requestClassification.isPending}
          onClick={() => {
            requestClassification.mutate();
          }}
        >
          {requestClassification.isPending ? t('pii.classifying') : t('pii.classifyNow')}
        </Button>
      </div>
    );
  }

  const approvedAndClean =
    classification.state === 'approved' && classification.verdicts.length === 0;
  const stale =
    latest !== undefined &&
    !verdictStillApplies(latest, classification.classified_against);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-2)',
        }}
      >
        <StatusBadge
          tone={
            classification.state === 'approved'
              ? 'ok'
              : classification.state === 'inconclusive'
                ? 'unknown'
                : classification.state === 'stale'
                  ? 'warn'
                  : 'info'
          }
          label={t(`review.state.${classification.state}.label`)}
          title={t(`review.state.${classification.state}.meaning`)}
        />
        <Button
          tone="secondary"
          busy={requestClassification.isPending}
          onClick={() => {
            requestClassification.mutate();
          }}
        >
          {requestClassification.isPending ? t('pii.classifying') : t('pii.classifyNow')}
        </Button>
      </div>

      {requestClassification.isError && (
        <div style={{ margin: 'var(--space-2) 0' }}>
          <Alert tone="error">{requestClassification.error.message}</Alert>
        </div>
      )}
      {requestClassification.isSuccess && (
        <div style={{ margin: 'var(--space-2) 0' }}>
          <Alert tone="notice">{t('pii.classifySuccess')}</Alert>
        </div>
      )}

      {stale && (
        /* Computed here from the two stored checksums rather than read from a server flag, so the
         * claim is one the client can justify. The server's own `stale` state is separate and may
         * not have been recomputed since the last harvest. */
        <p style={{ color: 'var(--status-warn)', margin: 'var(--space-2) 0 0' }}>
          {t('dataset.pii.checksumMoved')}
        </p>
      )}

      {/* `detail` is "the only actionable part of a failure" per its own field comment on the
       * wire, and it was computed and stored on every `inconclusive`/`stale` verdict while this
       * screen rendered nothing but a badge — so a classifier that failed (no model routed for
       * this tenant, an adapter exception, an empty proposal) looked identical to one that had
       * simply not been asked yet. */}
      {classification.detail != null && classification.detail !== '' && (
        <p
          className="lq-dim"
          style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}
        >
          {t('dataset.pii.reason', { reason: classification.detail })}
        </p>
      )}

      {classification.verdicts.length === 0 ? (
        <p className="lq-muted" style={{ margin: 'var(--space-2) 0 0' }}>
          {approvedAndClean ? t('review.pii.none') : t('review.pii.notAssessed')}
        </p>
      ) : (
        <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-4)' }}>
          {classification.verdicts.map((verdict) => (
            <li key={verdict.column_name}>
              <span className="lumiq-numeric">{verdict.column_name}</span>
              {' — '}
              <span className="lq-muted">{t(`pii.kind.${verdict.kind}`)}</span>
            </li>
          ))}
        </ul>
      )}

      {classification.approved_by !== null && (
        <p
          style={{
            color: 'var(--text-muted)',
            margin: 'var(--space-2) 0 0',
            fontSize: 'var(--text-xs)',
          }}
        >
          {t('review.previouslyApprovedBy', { who: classification.approved_by })}
        </p>
      )}
    </div>
  );
}

function SchemaHistory({
  versions,
}: {
  readonly versions: readonly DatasetVersion[];
}): ReactElement {
  const { t } = useTranslation();

  if (versions.length <= 1) {
    return (
      <p className="lq-muted" style={{ margin: 0 }}>
        {t('dataset.history.single')}
      </p>
    );
  }

  return (
    <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {versions.map((version, index) => {
        const previous = versions[index + 1];
        const changes = previous === undefined ? [] : diffVersions(version, previous);

        return (
          <li
            key={version.id}
            style={{
              borderLeft: '2px solid var(--border-subtle)',
              paddingLeft: 'var(--space-3)',
              paddingBottom: 'var(--space-3)',
            }}
          >
            <div
              style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}
            >
              <strong className="lumiq-numeric">v{version.version_number}</strong>
              <span
                style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}
              >
                {new Date(version.observed_at).toLocaleString()}
              </span>
            </div>
            {previous === undefined ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                {t('dataset.history.first')}
              </div>
            ) : (
              <ul
                style={{
                  margin: 'var(--space-1) 0 0',
                  paddingLeft: 'var(--space-4)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {changes.map((change) => (
                  <li key={`${change.kind}-${change.column}`}>
                    <span className="lumiq-numeric">{change.column}</span>{' '}
                    <span className="lq-muted">
                      {t(`dataset.change.${change.kind}`, {
                        before: change.before ?? '—',
                        after: change.after ?? '—',
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* How many distinct bound rules this panel will look up versions for.
 *
 * A ceiling rather than a guess: the set is the rules bound to *one* dataset, which is a handful in
 * every realistic estate, and a bound one cannot become a fan-out on a table somebody has bound
 * fifty rules to. Past the cap the extra rows still render with their name and kind — only the
 * column list reads as unresolved, which is the honest degradation. */
const MAX_RULE_LOOKUPS = 12;

/* Versions are immutable once published — a version's expression cannot change — so a long stale
 * time is correct rather than a saving: re-fetching would ask a question whose answer cannot move. */
const RULE_VERSION_STALE_MS = 5 * 60 * 1000;

function DatasetRulesPanel({
  datasetId,
  qualified,
}: {
  /** This dataset, so `Run` scopes the execution to it. See the note on the button below.
   *
   * **Threaded as a prop because the body already read it and the panel never had it.** The Run
   * control's own comment argues that omitting the dataset would scan the whole estate — correct
   * reasoning, wired to a name that was not in scope, so the Rules tab threw
   * `ReferenceError: datasetId is not defined` and the ErrorBoundary blanked the screen. */
  readonly datasetId: string;
  /** The dataset's qualified name, which is what a `name_glob` selector is matched against. */
  readonly qualified: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const bindings = useBindings();

  /* **This panel has always shown nothing, and the type error that revealed it was masked by a
   * `String()` around a string.** The filter compared a selector's kind to `'dataset_id'`, and
   * `SelectorKind` is `name_glob | tag | column_role` — there is no dataset-id selector, so the
   * predicate was always false.
   *
   * `name_glob` against the qualified name is the closest honest client-side answer: it is how a
   * steward writes a binding for one table. It is an *approximation* — `core.rules` owns selector
   * resolution and matches tags and column roles too, so this can under-report. Recorded rather
   * than papered over: the exact answer needs a "bindings for this dataset" route, which does not
   * exist, and inventing one client-side would put a second resolver in the browser. See
   * `model/selectors.ts` for why this can only under-report. */
  const boundRules = (bindings.data?.items ?? []).filter((binding) =>
    binding.selectors.some(
      (selector) =>
        selector.kind === 'name_glob' &&
        qualified !== null &&
        // `pattern` is optional on the wire — a `tag` selector carries none — so an absent one is
        // "matches nothing here" rather than a reason to throw.
        typeof selector.pattern === 'string' &&
        nameGlobMatches(selector.pattern, qualified),
    ),
  );

  /* **Every rule in the tenant, in one request, joined by id.** `RuleBinding` carries a `rule_id`
   * and no name, so without this the panel could only render the selector's own text — which is what
   * it did, and why two rules on one table were indistinguishable. One bounded listing rather than
   * one request per binding, and it is a request other screens have already made. */
  const rules = useRules('');
  const rulesById = new Map((rules.data?.items ?? []).map((rule) => [rule.id, rule]));

  /* **The columns a rule reads live on the *version*, not the rule**, so this is one read per
   * distinct bound rule. A fan-out, and bounded in the way this codebase permits: the set is the
   * rules bound to *one* dataset, capped below, and `useQueries` shares the cache key with the rule
   * screen's own versions read. The alternative is a `columns` field on `Rule` — a contract change
   * for a display join, recorded rather than taken. */
  const distinctRuleIds = [
    ...new Set(boundRules.map((binding) => binding.rule_id)),
  ].slice(0, MAX_RULE_LOOKUPS);
  const versions = useQueries({
    queries: distinctRuleIds.map((ruleId) => ({
      ...ruleVersionsQuery(ruleId),
      retry: false,
      staleTime: RULE_VERSION_STALE_MS,
    })),
  });

  const columnsByRuleId = new Map<string, readonly string[] | 'unresolved'>();
  for (const [index, result] of versions.entries()) {
    const ruleId = distinctRuleIds[index];
    /* `noUncheckedIndexedAccess` makes this `string | undefined`, and the guard is not ceremony:
     * pairing a result with the wrong rule would name the wrong columns, which is worse than naming
     * none. `approvals.ts` states the same rule for the same shape. */
    if (ruleId === undefined) continue;
    if (!result.isSuccess) {
      columnsByRuleId.set(ruleId, 'unresolved');
      continue;
    }
    /* The binding names a *version*; this listing is every version of the rule. Matching on the id
     * rather than taking the newest, because a binding pinned to 1.0.0 reads the columns 1.0.0
     * declared — showing the latest version's columns would describe a rule that is not the one
     * running. Falls back to `unresolved` rather than to another version's columns. */
    columnsByRuleId.set(ruleId, 'unresolved');
    for (const version of result.data.items) {
      const forBinding = boundRules.find(
        (binding) => binding.rule_version_id === version.id,
      );
      if (forBinding !== undefined) {
        columnsByRuleId.set(ruleId, version.expression.columns);
      }
    }
  }

  const rows = boundRules.map((binding) => {
    const rule = rulesById.get(binding.rule_id);
    const declared = columnsByRuleId.get(binding.rule_id) ?? 'unresolved';
    /* The binding's own "Target Column", when the steward filled it in. It wins over the rule's
     * declared columns because it is the narrower statement: the rule may read `amount` on every
     * table it is bound to, and this binding may have been pointed at one column of this one. */
    const targeted = binding.selectors
      .filter((selector) => selector.kind === 'column_role')
      .map((selector) => selector.role)
      .filter((role): role is string => typeof role === 'string' && role !== '');

    return {
      bindingId: binding.id,
      /* Carried so the Run control can refuse an unbound rule rather than letting the plan resolve to
         nothing and the route answer 409 about coverage — a failure about the wrong thing. */
      enabled: binding.enabled,
      ruleId: binding.rule_id,
      ruleName: rule?.name ?? null,
      kind: rule?.kind ?? null,
      severity: binding.severity,
      matchedBy: binding.selectors.map((selector) => selector.description).join(' or '),
      columns:
        targeted.length > 0
          ? targeted
          : declared === 'unresolved'
            ? ('unresolved' as const)
            : declared.length > 0
              ? declared
              : ('everyColumn' as const),
    };
  });

  return (
    /* A `Card`, where this drew its own `border: 1px solid var(--border-subtle)` box. The tokens
       were real, which is why it survived the theme change untouched and thereby *missed* it: the
       design system moved a card from an outline to an elevation, and every screen that drew its own
       kept the old treatment.

       **Read-only, deliberately.** This panel used to open `CreateBindingForm` in a `Modal` here
       too — the same form the Rules screen's own Bindings tab already opens, which is where
       binding creation lives now. Two places offering the identical form is the two-vocabularies
       shape this codebase keeps a name for, and here it cost more than a name: the inline form
       needed the bound-rules list to make room for it, which is the thing a reader came to this
       card to look at. */
    <Card
      title={t('dataset.rules.title')}
      hint={t('dataset.rules.count', { count: boundRules.length })}
    >
      {bindings.isPending ? (
        <LoadingState rows={2} label={t('dataset.rules.loading')} />
      ) : bindings.isError ? (
        /* **A failed read says so.** It rendered "Loading rules…" for ever on an error, because the
           branch below tested only `isPending` — so a 403 or a 429 read as a slow request. */
        <UnavailableState
          title={t('dataset.rules.unreadable')}
          guidance={bindings.error.message}
        />
      ) : boundRules.length === 0 ? (
        <EmptyState
          title={t('dataset.rules.none')}
          guidance={t('dataset.rules.noneGuidance')}
          action={
            <Link className="lq-btn lq-btn--primary" to={SCREEN_PATHS.rules}>
              {t('rules.bindings.create.open')}
            </Link>
          }
        />
      ) : (
        /* **A table, and the alignment is the reason.** This was a flex list of inline styles whose
           only content was `selector.description` — so two rules bound to one table both read "name
           matches gold.dimpayment", the severity badges floated at the far right of a wide card, and
           the one thing a steward came to find out (which rule, checking what, on which column) was
           the one thing absent. `.lq-table` gives every row the same four columns and the same
           baseline, which no amount of `justify-content` on a two-item flex row can. */
        <div className="lq-table-scroll">
          <table className="lq-table">
            <thead>
              <tr>
                <th scope="col">{t('dataset.rules.column.rule')}</th>
                <th scope="col">{t('dataset.rules.column.checks')}</th>
                <th scope="col">{t('dataset.rules.column.columns')}</th>
                <th scope="col">{t('dataset.rules.column.severity')}</th>
                <th scope="col" className="lq-table__actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.bindingId}>
                  <td>
                    {/* The rule's *name*, which is what a steward recognises. `null` means the
                        rules listing has not resolved it — a bounded read of 200 — so the row says
                        that rather than rendering a bare id as though it were a name. */}
                    {/* `PrimaryCell` rather than a `<br />` and a class of my own: the two-line
                        cell is a pattern every other table here uses, and my first version invented
                        `.lq-table__sub`, which is declared in no stylesheet — so the selector would
                        have rendered at body size with no styling at all. The selector text is the
                        secondary line because it answers "why is this rule on this table", which is
                        a real question and not the row's headline. */}
                    <PrimaryCell secondary={row.matchedBy}>
                      {/* The unresolved case keeps its own element, because it needs the `title` to
                          carry the id somebody debugging one row wants — so this is a ternary rather
                          than `row.ruleName ?? t(...)`, which would render the sentence as though it
                          were a name. eslint's `prefer-nullish-coalescing` fired on the previous
                          shape, which compared to `null` and returned a bare string on the other
                          branch; the two branches are different *elements* now, which is the honest
                          reason they cannot collapse to `??`. */}
                      {row.ruleName == null ? (
                        <span className="lq-muted" title={row.ruleId}>
                          {t('dataset.rules.unnamed')}
                        </span>
                      ) : (
                        <span>{row.ruleName}</span>
                      )}
                    </PrimaryCell>
                  </td>
                  <td>
                    {row.kind === null ? (
                      <span className="lq-muted">—</span>
                    ) : (
                      t(`rules.kind.${row.kind}.label`)
                    )}
                  </td>
                  <td>
                    {/* **Three states, because "no columns" means two different things.** A resolved
                        list is rendered in the mono face, since these are identifiers a reader
                        compares against the Schema tab. `unresolved` is a read that has not landed
                        or failed — never a dash, which would read as "this rule names none". And
                        `everyColumn` is the real answer for `not_null`/`unique` bound with no target
                        column: planning checks every column the dataset has, which is a fact worth
                        stating rather than a blank. */}
                    {row.columns === 'unresolved' ? (
                      <span className="lq-muted">
                        {t('dataset.rules.columnsUnknown')}
                      </span>
                    ) : row.columns === 'everyColumn' ? (
                      <span className="lq-muted">{t('dataset.rules.everyColumn')}</span>
                    ) : (
                      <span className="lq-mono">{row.columns.join(', ')}</span>
                    )}
                  </td>
                  <td>
                    <StatusBadge
                      tone={severityTone(row.severity)}
                      label={t(`rules.severity.${row.severity}.label`)}
                    />
                  </td>
                  <td className="lq-table__actions">
                    {/* **Scoped to this dataset as well as to this binding.** A binding written as a
                        name glob can resolve to hundreds of tables, so `Run` on *one* dataset's page
                        without `datasetId` would scan the estate — which is the opposite of what the
                        reader means and is a real cost on a customer's warehouse. */}
                    <RunBindingButton
                      bindingId={row.bindingId}
                      enabled={row.enabled}
                      datasetId={datasetId}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

type DatasetTab = 'schema' | 'classification' | 'profile' | 'rules' | 'scores';

export function DatasetDetail(): ReactElement {
  const { t } = useTranslation();
  const { datasetId = '' } = useParams<{ datasetId: string }>();
  const [tab, setTab] = useState<DatasetTab>('schema');

  const dataset = useDataset(datasetId);
  const history = useSchemaHistory(datasetId);
  const classification = useClassification(datasetId);
  /* A 404 is the ordinary answer for a dataset no execution has covered, so this query's error state
   * is a fact rather than a failure — rendered as "not scored yet" beside the name rather than as an
   * alert. See `ScoreLine`. */
  const score = useDatasetScore(datasetId);
  /* The source this dataset was harvested from, for the middle breadcrumb. The same cached list read
   * every other screen makes, so it costs nothing after the catalogue has been visited — which, now
   * that a dataset is reached *through* its source, is how a reader almost always arrives here. */
  const source = useConnection(dataset.data?.connection_id ?? '');
  /* The three reads behind the summary line, each independently absent. `useDatasetFacts` takes a
   * list because it is built for a listing's page; one id is the degenerate case and shares the
   * listing's cache entry when a reader arrives from `SourceDatasets`. */
  const facts = useDatasetFacts(dataset.data === undefined ? [] : [dataset.data.id]);
  const bindings = useBindings();
  /* The two actions. Both mint one idempotency key per dataset and clear it on success, so the
   * `disabled` on each button is what stops a double-click starting the work twice — see the header
   * for why that matters more for the profile than for the classification. */
  const classify = useRequestClassification(datasetId);
  const profile = useRequestProfile(datasetId);

  const qualified =
    dataset.data === undefined
      ? null
      : `${dataset.data.schema_name}.${dataset.data.object_name}`;
  const summaryClauses =
    dataset.data === undefined || qualified === null
      ? []
      : datasetSummaryClauses(
          {
            reading: profileReading(facts.data?.get(dataset.data.id), {
              unread: facts.isError,
            }),
            score: score.isSuccess ? { value: score.data.value } : null,
            bindings: bindingCount(bindings.data?.items, qualified, nameGlobMatches),
          },
          {
            rows: (count) => t('dataset.summary.rows', { count }),
            scored: (value) => t('dataset.summary.scored', { value }),
            unscored: t('dataset.summary.unscored'),
            bound: (count) => t('dataset.summary.bound', { count }),
            unbound: t('dataset.summary.unbound'),
          },
        );

  if (dataset.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('dataset.loading')}
        </p>
        <SkeletonRows rows={6} columns={4} />
      </>
    );
  }

  if (dataset.isError) {
    return (
      <>
        <Alert tone="error" title={t('dataset.unreachable')}>
          {dataset.error.message}
        </Alert>
        <p style={{ marginTop: 'var(--space-3)' }}>
          <Link to="/datasets">{t('dataset.backToCatalog')}</Link>
        </p>
      </>
    );
  }

  const versions = history.data?.items ?? [];
  const latest = versions[0];

  return (
    <section aria-labelledby="dataset-heading">
      <Breadcrumbs
        schema={dataset.data.schema_name}
        source={
          source.data === undefined
            ? null
            : { id: source.data.id, name: source.data.name }
        }
      />

      {/* **Tabs, where this was one long scroll of five stacked panels.** A dataset has five things
          to say about itself and a reader wants one of them; stacking them meant the profile — the
          most expensive thing on the page to produce — was also the furthest from the top.

          `Card`s rather than the hand-rolled `border: 1px solid var(--border-subtle)` divs this
          replaces. Those used real tokens, so they survived the theme change intact and *missed*
          it: the design system moved cards from an outline to an elevation, and a screen drawing
          its own borders kept the old treatment while every other screen changed. */}
      <div className="lq-page-header">
        <div>
          <h2 id="dataset-heading" className="lq-page-header__title lq-mono">
            {qualifiedName(dataset.data)}
          </h2>
          {/* **One summary line rather than a version count and a badge.** The three facts a reader
              wants before opening a tab are how big the table is, how it scores and how much of it
              is under a rule — and each clause is *omitted* rather than guessed when its figure is
              absent, so the line is short when we know little and never invents a number. See
              `datasetSummaryClauses`, where each omission is its own decision. */}
          <p className="lq-page-header__lede">
            {summaryClauses.length > 0 ? (
              summaryClauses.join(' \u00b7 ')
            ) : (
              <span className="lq-dim">{t('dataset.summary.nothingKnown')}</span>
            )}
          </p>
          {!tracksRenames(dataset.data) && (
            <p className="lq-muted">{t('catalog.noRenameAnchorDetail')}</p>
          )}
        </div>
        <div className="lq-page-header__actions">
          {/* **Two actions, and both are things this dataset can be asked to do.** The score badge
              that was here moved into the summary line above: it was a second rendering of a figure
              the line already carries, and a badge beside two buttons reads as a third control.

              `Classify PII` before `Run profile` in the DOM and on screen, cheapest first: a
              classification reads `information_schema` and a model, where a profile is 20 full scans
              of a customer's production table. Primary tone on the profile because it is the action
              this screen exists to offer, and the footnote under the panel is what states its cost. */}
          <Button
            tone="secondary"
            busy={classify.isPending}
            /* Refused while a classification is in flight *and* while one is already pending
               server-side. The second guard is the one that matters: `useRequestClassification`
               mints one idempotency key per dataset and clears it on success, so a second press
               after a success starts genuine new work. */
            disabled={classify.isPending}
            onClick={() => {
              classify.mutate();
            }}
          >
            {t('dataset.action.classify')}
          </Button>
          <Button
            tone="primary"
            busy={profile.isPending}
            disabled={profile.isPending}
            /* The cost, on the control itself. §9 records a double-clicked profile button buying 40
               full scans of a customer's table; `disabled` while pending is what closes the
               double-click, and the title says why the button is not free. */
            title={t('dataset.action.profileCost')}
            onClick={() => {
              profile.mutate();
            }}
          >
            {t('dataset.action.profile')}
          </Button>
          {/* Lineage is a link rather than a tab, because it is a different screen: the graph needs
              the full width and its own pan/zoom, and embedding it in a 340px panel would make it
              unusable to save a navigation.

              `datasetId`, not `dataset` — `LineageScreen` reads `searchParams.get('datasetId')`.
              The two names had drifted: this link pointed at a parameter the screen never read, so
              every "View lineage ↗" from a dataset landed on the picker instead of the node the
              reader had just been looking at. */}
          <Link to={`${SCREEN_PATHS.lineage}?datasetId=${encodeURIComponent(datasetId)}`}>
            {t('dataset.tab.lineage')}
            <span aria-hidden="true"> ↗</span>
          </Link>
        </div>
      </div>

      <Tabs
        label={t('dataset.tabs.label')}
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'schema', label: t('dataset.tab.schema'), count: latest?.columns.length },
          { id: 'classification', label: t('dataset.tab.classification') },
          { id: 'profile', label: t('dataset.tab.profile') },
          { id: 'rules', label: t('dataset.tab.rules') },
          { id: 'scores', label: t('dataset.tab.scores') },
        ]}
      />

      {tab === 'schema' && (
        <div className="lq-stack">
          <Card title={t('dataset.columns')}>
            {latest === undefined ? (
              <EmptyState
                title={t('dataset.noVersions')}
                guidance={t('dataset.noVersionsGuidance')}
              />
            ) : (
              <DataTable
                caption={t('dataset.columnsCaption')}
                columns={[
                  { key: 'name', label: t('dataset.column.name') },
                  { key: 'type', label: t('dataset.column.type') },
                  { key: 'nullable', label: t('dataset.column.nullable') },
                ]}
              >
                {latest.columns.map((column) => (
                  <tr key={column.name}>
                    <td>
                      <PrimaryCell
                        {...(column.comment === null
                          ? {}
                          : { secondary: column.comment })}
                      >
                        <span className="lq-mono">{column.name}</span>
                      </PrimaryCell>
                    </td>
                    <td className="lq-muted lq-mono">{column.data_type}</td>
                    <td className="lq-muted">
                      {column.nullable ? t('dataset.nullable') : t('dataset.notNullable')}
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
          </Card>

          <Card title={t('dataset.history.title')}>
            {history.isPending ? (
              <p role="status" className="lq-muted">
                {t('dataset.history.loading')}
              </p>
            ) : history.isError ? (
              <Alert tone="error">{history.error.message}</Alert>
            ) : (
              <SchemaHistory versions={versions} />
            )}
          </Card>
        </div>
      )}

      {tab === 'classification' && (
        <Card title={t('dataset.pii.title')}>
          {classification.isPending ? (
            <p role="status" className="lq-muted">
              {t('dataset.pii.loading')}
            </p>
          ) : classification.isError ? (
            <Alert tone="error">{classification.error.message}</Alert>
          ) : (
            <PiiPanel
              classification={classification.data}
              latest={latest}
              datasetId={datasetId}
            />
          )}
        </Card>
      )}

      {tab === 'profile' && (
        <Card title={t('profile.title')}>
          {/* The current version's column count is what "finished" means for a profile, and this
              screen already has it for the Schema tab's badge. Passing it lets the panel poll while
              a profile is running and stop the moment the stored count reaches it — a bound derived
              from the work rather than a timer somebody guessed. */}
          <ProfilePanel datasetId={datasetId} expectedColumns={latest?.columns.length} />
        </Card>
      )}

      {tab === 'rules' && (
        <DatasetRulesPanel
          datasetId={datasetId}
          qualified={qualifiedName(dataset.data)}
        />
      )}

      {/* The score, owned by `features/scoring`. A tab rather than the header badge it replaces:
          the value alone was all the header could carry, and the decomposition — which rule cost
          what — is the half a reader acts on. */}
      {tab === 'scores' && (
        <>
          <DatasetScorePanel datasetId={datasetId} />
          {/* **Below the rule score, never blended with it.** A rule score measures declared
              expectations against real counts and is authoritative; this is what statistics alone
              can say, and it demotes to context the moment one exists. Where none does — a profiled
              dataset with no rules bound — the panel above is a short empty state and this becomes
              the substance of the tab, which is the case that put it here: profiling produced twelve
              columns of findings and this tab said only "not scored yet". */}
          <ProfileBaselineCard datasetId={datasetId} />
        </>
      )}
    </section>
  );
}
